import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";

import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import MonthYearSelector from "./components/MonthYearSelector";

import Dashboard from "./pages/Dashboard";
import Expenses from "./pages/Expenses";
import Income from "./pages/Income";
import Budget from "./pages/Budget";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Login from "./pages/Login";
import DemoLogin from "./pages/DemoLogin";
import LandingPage from "./pages/LandingPage";
import { clearDemoModeFlag, isDemoAuthenticated } from "./utils/demoAuth";
import { DEMO_STORAGE_EVENT } from "./utils/demoStorage";
import { getCurrentMonth, getCurrentYear } from "./utils/monthYear";

function isTokenValid(token) {
  if (!token) return false;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));

    if (!payload.exp) return true;

    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function ProtectedLayout({ demo = false }) {
  const token = localStorage.getItem("token");
  const [demoRefreshKey, setDemoRefreshKey] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const savedMonth = Number(localStorage.getItem("selectedMonth"));
    return savedMonth >= 1 && savedMonth <= 12 ? savedMonth : getCurrentMonth();
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    const savedYear = Number(localStorage.getItem("selectedYear"));
    return savedYear >= 1900 ? savedYear : getCurrentYear();
  });

  useEffect(() => {
    localStorage.setItem("selectedMonth", selectedMonth);
    localStorage.setItem("selectedYear", selectedYear);
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (!demo) return undefined;

    const refreshDemoPages = () => {
      setDemoRefreshKey((key) => key + 1);
    };

    window.addEventListener(DEMO_STORAGE_EVENT, refreshDemoPages);
    return () => window.removeEventListener(DEMO_STORAGE_EVENT, refreshDemoPages);
  }, [demo]);

  if (demo) {
    localStorage.setItem("demoMode", "true");
    localStorage.setItem("isDemoMode", "true");
  } else {
    clearDemoModeFlag();
  }

  if (demo && !isDemoAuthenticated()) {
    return <Navigate to="/demo-login" replace />;
  }

  if (!demo && !isTokenValid(token)) {
    localStorage.removeItem("token");

    return <Navigate to="/login" replace />;
  }

  const monthYearProps = {
    selectedMonth,
    selectedYear,
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-area">
        <Topbar>
          <MonthYearSelector
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            setSelectedMonth={setSelectedMonth}
            setSelectedYear={setSelectedYear}
          />
        </Topbar>

        <main className="page-content">
          <Routes key={demo ? demoRefreshKey : "personal"}>
            <Route
              index
              element={<Navigate to={demo ? "/demo/dashboard" : "/dashboard"} replace />}
            />
            <Route path="demo" element={<Navigate to="/demo/dashboard" replace />} />
            <Route
              path="dashboard"
              element={<Dashboard {...monthYearProps} />}
            />
            <Route
              path="expenses"
              element={<Expenses {...monthYearProps} />}
            />
            <Route
              path="income"
              element={<Income {...monthYearProps} />}
            />
            <Route
              path="budget"
              element={<Budget {...monthYearProps} />}
            />
            <Route
              path="reports"
              element={<Reports {...monthYearProps} />}
            />
            <Route path="settings" element={<Settings />} />
            <Route
              path="*"
              element={<Navigate to={demo ? "/demo/dashboard" : "/dashboard"} replace />}
            />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function DemoProtectedRoute() {
  return <ProtectedLayout demo />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/demo-login" element={<DemoLogin />} />
        <Route path="/demo/*" element={<DemoProtectedRoute />} />
        <Route path="/*" element={<ProtectedLayout />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
